import {
  ChatContainerContent,
  ChatContainerRoot,
  ChatContainerScrollAnchor,
} from "@arcadeai/design-system/components/ui/atoms/chat-container";
import { ScrollButton } from "@arcadeai/design-system/components/ui/atoms/scroll-button";
import {
  ChatMessageList,
  ChatStreamingIndicator,
} from "@arcadeai/design-system/components/ui/molecules/chat-message-list";
import { cn } from "@arcadeai/design-system/lib/utils";
import type { UIMessage } from "ai";
import type { ReactNode } from "react";
import { useChatRuntime } from "../context";
import { ChatMessage } from "./chat-message";

function shouldShowStreamingIndicator(
  messages: UIMessage[],
  isStreaming: boolean
): boolean {
  if (!isStreaming) {
    return false;
  }
  const lastMessage = messages.at(-1);
  // Only show the standalone indicator while no assistant message exists yet
  // (the "submitted" phase). Once the assistant message appears, it renders
  // its own inline indicator so we avoid the gap-8 layout shift.
  return !lastMessage || lastMessage.role !== "assistant";
}

export type ChatMessagesProps = {
  emptyState?: ReactNode;
  className?: string;
};

export function ChatMessages({ emptyState, className }: ChatMessagesProps) {
  const chat = useChatRuntime();
  const hasMessages = chat.messages.length > 0;
  const showLoader = shouldShowStreamingIndicator(
    chat.messages,
    chat.isStreaming
  );

  if (!hasMessages) {
    return (
      <div className={cn("flex min-w-0 flex-1 flex-col", className)}>
        {emptyState}
      </div>
    );
  }

  return (
    <ChatContainerRoot
      className={cn("relative min-w-0 flex-1 flex-col pt-4", className)}
      key={chat.chatId}
    >
      <ChatContainerContent className="gap-6 pb-4">
        <ChatMessageList>
          {chat.messages.map((message, index) => (
            <ChatMessage
              authInfoMap={chat.authInfoMap}
              isLast={index === chat.messages.length - 1}
              isStreaming={
                chat.isStreaming && index === chat.messages.length - 1
              }
              key={message.id}
              message={message}
              onRetryAuth={chat.retryAuth}
            />
          ))}
          {showLoader && <ChatStreamingIndicator />}
        </ChatMessageList>
        <ChatContainerScrollAnchor />
      </ChatContainerContent>
      <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-10 bg-linear-to-t from-10% from-background to-transparent" />
      <div className="pointer-events-none absolute right-0 bottom-4 left-0 z-20 flex justify-center px-4">
        <div className="flex w-full max-w-3xl justify-end pr-4">
          <ScrollButton className="pointer-events-auto shadow-md" />
        </div>
      </div>
    </ChatContainerRoot>
  );
}
